import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Box, Button, Popover, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';

import * as API from 'api/gateways';
import Field from 'components/Field';
import GatewayDevices from 'components/GatewayDevices';
import { GATEWAY_TYPE } from 'enum/gateway';
import { generateKeypair } from 'utils/wireguardUtils';
import { updateGateway } from 'store/objects/actions';
import { acceptCommit } from 'store/common-scenario-actions';

import { downloadConfig } from './utils';

const ConfigOptions = ({ data, addresses }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { cusID: customerUUID, ecoID: ecosystemUUID } = useParams();
  const ecosystem = useSelector((state) => state.ecosystems.currentEcosystem);

  const [anchorEl, setAnchorEl] = useState(null);
  const [acretoPrivateKey, setAcretoPrivateKey] = useState('');

  const handleGenerateClientKeys = async (event) => {
    event.preventDefault();
    setAnchorEl(null);
    const { publicKey: peerId, privateKey } = generateKeypair();
    setAcretoPrivateKey(privateKey);
    try {
      const payload = {
        ...data,
        peer_id: peerId,
      };
      const result = await API.updateGateway({ customerUUID, ecosystemUUID, uuid: data.uuid, payload });
      dispatch(updateGateway({ ecosystemUUID, result }));
    } catch (err) {
      const errMsg = err.response?.data?.message || 'Failed to generate new configuration';
      enqueueSnackbar(errMsg, { variant: 'error' });
    }
  };

  const openClientKeyPop = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const closeClientKeyPop = () => {
    setAnchorEl(null);
  };

  if (data.gateway_type !== GATEWAY_TYPE.WIREGUARD) {
    return (
      <div className="gateway__detail-config flex-one space-above">
        <Field.Group label="Configuration Options">
          <GatewayDevices data={data} />
        </Field.Group>
      </div>
    );
  }

  const handleDownload = () => {
    downloadConfig({ gateway: data, addresses, ecosystem, acretoPrivateKey });
  };

  return (
    <div className="gateway__detail-config flex-one space-above">
      <Field.Group label="Configuration">
        <div>
          {data.server_id ? (
            <div>
              {acretoPrivateKey ? (
                <div className="key-actions">
                  <div className="space-above-8 pb-5">
                    <p>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleDownload}
                        sx={{ textTransform: 'unset', fontSize: 16, mt: 1 }}
                      >
                        Download configuration
                      </Button>
                    </p>
                  </div>
                  <p>Download and install the configuration in the WireGuard client app.</p>
                  <p className="commit-msg">
                    The new configuration will be activated once you &nbsp;
                    <span
                      className="link"
                      onClick={() => {
                        dispatch(acceptCommit());
                      }}
                    >
                      apply your changes
                    </span>
                  </p>
                </div>
              ) : (
                <div className="key-actions">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={openClientKeyPop}
                    sx={{ textTransform: 'unset', fontSize: 16, mt: 2 }}
                  >
                    Generate new private and public key
                  </Button>
                  <Popover
                    id="popover-clientKey"
                    open={!!anchorEl}
                    anchorEl={anchorEl}
                    onClose={closeClientKeyPop}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'center',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'center',
                    }}
                  >
                    <Box className="popover-clientkey">
                      <p>
                        Note: Once you generate a new configuration, the old configuration and keys will be replaced.
                      </p>
                      <p>
                        Your active WireGuard connections will disconnect until the new configuration is installed and
                        applied.
                      </p>
                      <p className="confirm">Are you sure?</p>
                      <div className="generate_buttons">
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleGenerateClientKeys}
                          data-testid="generate-clientkey"
                        >
                          Yes
                        </Button>
                        <Button variant="outlined" onClick={closeClientKeyPop}>
                          No
                        </Button>
                      </div>
                    </Box>
                  </Popover>
                </div>
              )}
            </div>
          ) : (
            <div>
              <p className="commit-msg">
                <Typography
                  component="span"
                  color="primary"
                  sx={{ ':hover': { textDecoration: 'underline', cursor: 'pointer' } }}
                  onClick={() => dispatch(acceptCommit())}
                >
                  Apply your changes
                </Typography>
                &nbsp;to generate configuration
              </p>
            </div>
          )}
        </div>
      </Field.Group>
    </div>
  );
};

ConfigOptions.propTypes = {
  data: PropTypes.object,
  addresses: PropTypes.array,
};

export default ConfigOptions;
